import PlusOutlined from '@ant-design/icons/PlusOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import List from 'antd/es/list';
import message from 'antd/es/message';
import Skeleton from 'antd/es/skeleton';
import Typography from 'antd/es/typography';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ContentContainer from '@components/ContentContainer';
import { GradientText } from '@components/GradientText';
import { apiClient } from '@queries/axios';
import { useTeamUsersQuery } from '@queries/team-users';
import { useTeamsQuery } from '@queries/teams';
import { useSessionUserId } from '@utils/hooks';

const { Title, Paragraph, Text } = Typography;

const CreateTeamContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: clamp(2rem, 10vh, 20vh);
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
`;

const WelcomeSection = styled.div`
  margin-bottom: ${({ theme }) => theme.token.margin * 3}px;
  text-align: center;
`;

const TeamsListSection = styled.div`
  margin-top: ${({ theme }) => theme.token.margin * 3}px;
`;

const TeamCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.token.marginMD}px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
    border-color: ${({ theme }) => theme.token.colorPrimary};
  }
`;

interface CreateTeamFormValues {
  name: string;
  description?: string;
}

const TeamCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<CreateTeamFormValues>();
  const { userId: sessionUserId } = useSessionUserId();

  // Fetch team users for the current user
  const teamUsersQuery = useTeamUsersQuery(
    { userIds: sessionUserId ? [sessionUserId] : [] },
    !!sessionUserId,
  );

  // Get team IDs from team users
  const teamIds = teamUsersQuery.data?.map((teamUser) => teamUser.teamId) || [];

  // Fetch teams data
  const teamsQuery = useTeamsQuery({ teamIds }, !!teamIds.length);

  const createTeamMutation = useMutation({
    mutationFn: async (values: CreateTeamFormValues) => {
      const response = await apiClient.post('/teams', values);
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['teams'] });
      void message.success('Team created successfully');
      navigate(`/team/${data.teamId}`);
    },
    onError: () => {
      void message.error('Failed to create team');
    },
  });

  const handleSubmit = (values: CreateTeamFormValues) => {
    void createTeamMutation.mutate(values);
  };

  const handleTeamClick = (teamId: string) => {
    navigate(`/team/${teamId}`);
  };

  return (
    <ContentContainer showFullWidthToggle={false}>
      <CreateTeamContainer>
        <WelcomeSection>
          <Title level={2}>
            Welcome! Let's create a new{' '}
            <GradientText $isAnimated={false}>Team</GradientText>
          </Title>
          <Paragraph>
            Set up your team workspace to get started with Catalyzed.
          </Paragraph>
        </WelcomeSection>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={createTeamMutation.isPending}
        >
          <Form.Item
            label="Team Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter your team name' },
              { min: 3, message: 'Team name must be at least 3 characters' },
              { max: 50, message: 'Team name cannot exceed 50 characters' },
            ]}
          >
            <Input
              prefix={<TeamOutlined />}
              placeholder="Enter your team name"
              size="large"
            />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[
              { max: 200, message: 'Description cannot exceed 200 characters' },
            ]}
          >
            <Input.TextArea
              placeholder="Briefly describe your team's purpose"
              rows={4}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              loading={createTeamMutation.isPending}
              size="large"
              block
            >
              Create Team
            </Button>
          </Form.Item>
        </Form>

        <TeamsListSection>
          <Title level={4}>Your Existing Teams</Title>
          <Paragraph>
            You are already a member of the following teams. Click on a team to
            navigate to it.
          </Paragraph>

          {teamsQuery.isLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : teamsQuery.data && teamsQuery.data.length > 0 ? (
            <List
              dataSource={teamsQuery.data}
              renderItem={(team) => (
                <TeamCard onClick={() => handleTeamClick(team.teamId)}>
                  <Card.Meta avatar={<TeamOutlined />} title={team.name} />
                </TeamCard>
              )}
            />
          ) : (
            <Text type="secondary">You are not a member of any teams yet.</Text>
          )}
        </TeamsListSection>
      </CreateTeamContainer>
    </ContentContainer>
  );
};

export default TeamCreatePage;
